import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Create_ContextualAiTool } from "@mirage/analytics/events/types/create_contextual_ai_tool";
import { useAvailableModels } from "@mirage/service-conversation/hooks/useAvailableModels";
import type {
  AgentId,
  WorkflowStepValuesType,
  WorkflowValues,
} from "@mirage/service-conversation/types";
import i18n from "@mirage/translations";
import { memo, useCallback, useState } from "react";
import { ExpandableCard } from "./ExpandableCard";
import { ToolTypeSelector } from "./ToolTypeSelector";
import type { WorkflowStep } from "./types";
import { WorkflowStep as WorkflowStepComponent } from "./WorkflowStep";

interface WorkflowStepCardProps {
  stepDef: WorkflowStep;
  currentStep: number;
  expandedStep: number | undefined;
  selectedTool: AgentId;
  workflowValues: WorkflowValues;
  agentId?: string;
  onExpandedStepChange: (step: number | undefined) => void;
  onSelectedToolChange: (tool: AgentId) => void;
  onStepSubmit: (step: number, values: WorkflowStepValuesType) => Promise<void>;
  onStepBack: () => void;
  onExitWorkflowAgent: () => void;
  onStepPreview: (
    step: number,
    values: WorkflowStepValuesType,
  ) => Promise<void>;
}

export const WorkflowStepCard = memo(
  ({
    stepDef,
    currentStep,
    expandedStep,
    selectedTool,
    workflowValues,
    agentId,
    onExpandedStepChange,
    onSelectedToolChange,
    onStepSubmit,
    onStepBack,
    onExitWorkflowAgent,
    onStepPreview,
  }: WorkflowStepCardProps) => {
    const { reportPapEvent } = useMirageAnalyticsContext();
    const { availableModels } = useAvailableModels();
    const [isSubmitted, setIsSubmitted] = useState(
      agentId && agentId !== "new",
    );
    const isFirstStep = stepDef.step === 1;

    const stepValues = workflowValues[stepDef.step];

    const getSubtitle = useCallback(() => {
      if (stepDef.step === 1) {
        const typeId = stepValues["type"] as string;
        const typeOption = stepDef.inputs[0].availableOptions?.find(
          (option) => option.id === typeId,
        );
        return typeOption?.label;
      } else if (stepDef.step === 2) {
        const modelId = stepValues["base-model"] as string;
        const model = availableModels.models.find((m) => m.id === modelId);
        return `${model?.displayName || modelId}, ${stepValues["prompt"]}`;
      } else if (stepDef.step === 3) {
        return i18n.t("compose_num_steps", {
          count: Object.keys(stepValues).length,
        });
      } else if (stepDef.step === 4) {
        return i18n.t("compose_num_sources", {
          count: (stepValues["sources"] as string[]).length,
        });
      }
      return undefined;
    }, [stepValues, availableModels, stepDef.step]);

    return (
      <ExpandableCard
        key={stepDef.step}
        step={stepDef.step}
        title={stepDef.name}
        active={currentStep === stepDef.step}
        expanded={expandedStep === stepDef.step}
        completed={currentStep > stepDef.step}
        toggleExpanded={() => {
          onExpandedStepChange(
            expandedStep === stepDef.step ? undefined : stepDef.step,
          );
        }}
        toggleDisabled={
          !isSubmitted &&
          !(agentId && agentId !== "new") &&
          currentStep !== stepDef.step
        }
        subtitle={isSubmitted ? getSubtitle() : undefined}
      >
        {isFirstStep ? (
          <ToolTypeSelector
            step={stepDef}
            currentValue={selectedTool}
            onCancel={onExitWorkflowAgent}
            onSubmit={(step, tool) => {
              onSelectedToolChange(tool as AgentId);
              reportPapEvent(
                PAP_Create_ContextualAiTool({
                  aiToolType: "chat", // Update this when we have more tool types
                  aiToolCreationFlow: "select_type",
                  featureLine: "assist",
                  actionSurfaceComponent: "ai_tool_creation",
                  dashActionSurface: "conversation_page",
                }),
              );
              onStepSubmit(step, { type: tool });
              setIsSubmitted(true);
            }}
          />
        ) : (
          <WorkflowStepComponent
            step={stepDef}
            currentValues={workflowValues[stepDef.step]}
            onCancel={onStepBack}
            onSubmit={async (values) => {
              setIsSubmitted(true);
              await onStepSubmit(stepDef.step, values);
            }}
            onPreview={async (values) => {
              await onStepPreview(stepDef.step, values);
            }}
          />
        )}
      </ExpandableCard>
    );
  },
);

WorkflowStepCard.displayName = "WorkflowStepCard";
